"use client";

import { useAuth, UserButton } from "@clerk/clerk-react";
import {
  Ban,
  BarChart3,
  BriefcaseBusiness,
  Calendar,
  ChevronDownIcon,
  ClipboardList,
  CreditCard,
  DollarSign,
  FileText,
  Handshake,
  Landmark,
  LayoutDashboard,
  Package,
  PackageCheck,
  Settings,
  ShoppingCart,
  Ticket,
  TrendingUp,
  User as UserIcon,
  Users,
} from "lucide-react";
import * as React from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { User } from "@/core";
import { useUser } from "@/hooks";
import { useUserStore } from "@/stores/user";
import { ModeToggle } from "../ui/mode-toggle";
import { useTheme } from "../ui/theme-provider";

const data = {
  user: {
    name: "Admin User",
    email: "admin@themiles.market",
    avatar: "/avatars/admin.jpg",
  },
  navMain: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Products",
      url: "/products",
      icon: Package,
    },
    {
      title: "Leads",
      url: "/leads",
      icon: UserIcon,
    },

    {
      title: "Deals",
      url: "/deals",
      icon: Handshake,
    },
    {
      title: "Promo Codes",
      url: "/promo-codes",
      icon: Ticket,
    },
    {
      title: "Operations",
      icon: PackageCheck,
      items: [
        {
          title: "Suggestions",
          url: "/operations/order-suggestions",
          icon: ClipboardList,
        },
        {
          title: "Purchase Orders",
          url: "/operations/purchase-orders",
          icon: Package,
        },
        {
          title: "Vendors",
          url: "/vendors",
          icon: BriefcaseBusiness,
        },
        {
          title: "Inventory",
          url: "/operations/inventory",
          icon: PackageCheck,
        },
        {
          title: "Sales Orders",
          url: "/operations/sales-orders",
          icon: Handshake,
        },
        {
          title: "Clients",
          url: "/clients",
          icon: Users,
        },
        {
          title: "Finance",
          url: "/operations/finance",
          icon: Calendar,
        },
        {
          title: "Transactions",
          url: "/operations/payments",
          icon: CreditCard,
        },
        {
          title: "Balances",
          url: "/operations/balances",
          icon: Landmark,
          fullAccessOnly: true,
        },
        {
          title: "Ops Reports",
          url: "/operations/reports",
          icon: BarChart3,
        },
      ],
    },
    {
      title: "Reports",
      icon: ClipboardList,
      items: [
        {
          title: "Products",
          url: "/reports/products",
          icon: BarChart3,
        },
        {
          title: "Monthly",
          url: "/reports/monthly",
          icon: Calendar,
        },
        {
          title: "Inventory Value",
          url: "/reports/inventory-value",
          icon: DollarSign,
        },
        {
          title: "Inventory Throughput",
          url: "/reports/inventory",
          icon: PackageCheck,
        },
        {
          title: "Purchase Orders",
          url: "/reports/purchase-orders",
          icon: ShoppingCart,
        },
        {
          title: "Sales",
          url: "/reports/sales",
          icon: TrendingUp,
        },
      ],
    },
    {
      title: "Assistive Reports",
      icon: ClipboardList,
      items: [
        {
          title: "Stock Check Guide",
          url: "/reports/stock-check-guide",
          icon: PackageCheck,
        },
      ],
    },
    {
      title: "Configuration",
      icon: Settings,
      adminOnly: true,
      items: [
        {
          title: "Note Templates",
          url: "/operations/note-templates",
          icon: FileText,
        },
        {
          title: "Blocked Contacts",
          url: "/operations/blocked-contacts",
          icon: Ban,
        },
      ],
    },
  ],
};

type NavLeaf = {
  title: string;
  url: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  fullAccessOnly?: boolean;
};
type NavGroup = {
  title: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  fullAccessOnly?: boolean;
  items: NavEntry[];
};
type NavEntry = NavLeaf | NavGroup;

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return "items" in entry;
}

function collectUrls(entries: NavEntry[]): string[] {
  return entries.flatMap((entry) =>
    isNavGroup(entry) ? collectUrls(entry.items) : [entry.url],
  );
}

function NavMain({ items }: { items: NavEntry[] }) {
  const location = useLocation();
  const isAdmin = useUserStore((state) => state.isAdmin());
  const hasFullAccess = useUserStore((state) => state.hasFullAccess());
  const [openItems, setOpenItems] = React.useState<string[]>([]);

  const canSee = (entry: NavEntry) =>
    (isAdmin || !("adminOnly" in entry && entry.adminOnly)) &&
    (hasFullAccess || !("fullAccessOnly" in entry && entry.fullAccessOnly));

  const visibleItems = items.filter(canSee);

  const isActivePage = (href: string) => {
    return (
      location.pathname === href ||
      (href === "/dashboard" && location.pathname === "/")
    );
  };

  const groupHasActive = (group: NavGroup): boolean =>
    collectUrls(group.items).some((url) => isActivePage(url));

  const toggleItem = (title: string) => {
    setOpenItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title],
    );
  };

  React.useEffect(() => {
    // Auto-open every group (at any depth) that contains the active page.
    const toOpen: string[] = [];
    const walk = (entries: NavEntry[]) => {
      for (const entry of entries) {
        if (isNavGroup(entry)) {
          if (groupHasActive(entry)) toOpen.push(entry.title);
          walk(entry.items);
        }
      }
    };
    walk(items);
    if (toOpen.length) {
      setOpenItems((prev) => Array.from(new Set([...prev, ...toOpen])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Renders the children of a group: leaf links, or a nested collapsible group.
  const renderSubEntries = (entries: NavEntry[]) =>
    entries.filter(canSee).map((entry) => {
      if (isNavGroup(entry)) {
        const isOpen = openItems.includes(entry.title);
        return (
          <SidebarMenuSubItem key={entry.title}>
            <SidebarMenuSubButton
              onClick={() => toggleItem(entry.title)}
              isActive={groupHasActive(entry)}
              className="cursor-pointer"
            >
              <entry.icon className="h-4 w-4" />
              <span>{entry.title}</span>
              <ChevronDownIcon
                className={`ml-auto h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                  }`}
              />
            </SidebarMenuSubButton>
            {isOpen && (
              <SidebarMenuSub>{renderSubEntries(entry.items)}</SidebarMenuSub>
            )}
          </SidebarMenuSubItem>
        );
      }

      return (
        <SidebarMenuSubItem key={entry.title}>
          <SidebarMenuSubButton asChild isActive={isActivePage(entry.url)}>
            <Link to={entry.url}>
              <entry.icon className="h-4 w-4" />
              <span>{entry.title}</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    });

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Navigation</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) => {
            if (isNavGroup(item)) {
              const isOpen = openItems.includes(item.title);
              const hasActiveChild = groupHasActive(item);

              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    onClick={() => toggleItem(item.title)}
                    isActive={hasActiveChild}
                    className="group/menu-button"
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                    <ChevronDownIcon
                      className={`ml-auto h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                        }`}
                    />
                  </SidebarMenuButton>
                  {isOpen && (
                    <SidebarMenuSub>
                      {renderSubEntries(item.items)}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              );
            }

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={isActivePage(item.url)}>
                  <Link to={item.url}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function NavUser({ user }: { user: User }) {
  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: "h-8 w-8",
                    userButtonPopoverCard: "bg-card border-border",
                    userButtonPopoverActionButton:
                      "text-foreground hover:bg-accent",
                  },
                }}
              />
              <div className="flex flex-col text-sm">
                <span className="text-muted-foreground text-sm">
                  {user.email.length > 18
                    ? `${user.email.slice(0, 18)}...`
                    : user.email}
                </span>
              </div>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { userId } = useAuth();
  const user = useUser(userId as string);
  const { theme } = useTheme();
  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5 h-full w-fit"
            >
              <Link to="/dashboard">
                {theme == "light" ? (
                  <img
                    src="/tmm-dark-logo.svg"
                    alt="The Miles Market"
                    className="h-12 w-auto"
                  />
                ) : (
                  <img
                    src="/tmm-white-logo.svg"
                    alt="The Miles Market"
                    className="h-12 w-auto"
                  />
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain as NavEntry[]} />
      </SidebarContent>
      <SidebarFooter className="flex items-center justify-between flex-row">
        {user.data && <NavUser user={user.data} />}
        <ModeToggle />
      </SidebarFooter>
    </Sidebar>
  );
}
